import {
  ArrowPathIcon,
  BanknotesIcon,
  CheckCircleIcon,
  DocumentArrowDownIcon,
  ShieldExclamationIcon,
  XCircleIcon,
} from "@heroicons/react/20/solid";
import { toast } from "sonner";
import { useTranslations } from "use-intl";
import { IncidentClosedOutcome, IncidentStatus } from "@/gql/graphql";
import { ActionsHook } from "@/lib/Components/Actions/ActionGroupButtons";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { useConfirmDeleteDialog } from "@/lib/Components/Dialog/Hooks/useConfirmDeleteDialog";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { IncidentRecord } from "@/app/Incidents/GraphQL/incidentRecordQuery";
import { closeIncidentMutation } from "@/app/Incidents/GraphQL/closeIncidentMutation";
import { generateIncidentPdfMutation } from "@/app/Incidents/GraphQL/generateIncidentPdfMutation";
import { OpenIncidentClaimDialog } from "@/app/Incidents/Components/Dialogs/OpenIncidentClaimDialog";
import { IncidentReasonDialog } from "@/app/Incidents/Components/Dialogs/IncidentReasonDialog";

export function useIncidentActions(): ActionsHook<IncidentRecord> {
  const t = useTranslations("incident");
  const { mutateAsync: closeIncident } = useGqlMutation(closeIncidentMutation);
  const { mutateAsync: generatePdf } = useGqlMutation(
    generateIncidentPdfMutation,
  );
  const { open: openClaimDialog } = useDialog(OpenIncidentClaimDialog);
  const { open: openReasonDialog } = useDialog(IncidentReasonDialog);
  const { open: openConfirmDialog } = useConfirmDeleteDialog();

  return (incident) => {
    const status = incident.status;

    function confirmClose(outcome: IncidentClosedOutcome) {
      const key = outcome === IncidentClosedOutcome.Paid ? "pay" : "noClaim";

      openConfirmDialog({
        title: t(`decision.${key}.title`),
        message: t(`decision.${key}.message`, {
          amount: formatCurrency(incident.excess_amount, incident.currency),
        }),
        buttonText: t(`decision.${key}.confirm`),
        onDelete: async () => {
          await closeIncident({ id: incident.id, outcome });
          toast.success(t(`decision.${key}.done`));
        },
      });
    }

    return [
      {
        label: t("actions.actions"),
        items: [
          {
            actions: [
              {
                label: t("actions.openClaim"),
                Icon: ShieldExclamationIcon,
                color: "orange",
                isHidden: status !== IncidentStatus.Reported,
                handleAction: () =>
                  openClaimDialog({
                    incidentId: incident.id,
                    currency: incident.currency,
                  }),
              },
              {
                label: t("actions.payClaim"),
                Icon: BanknotesIcon,
                color: "green",
                isAdminAction: true,
                isHidden: status !== IncidentStatus.ClaimOpen,
                handleAction: () => confirmClose(IncidentClosedOutcome.Paid),
              },
              {
                label: t("actions.rejectClaim"),
                Icon: XCircleIcon,
                color: "red",
                isAdminAction: true,
                isHidden: status !== IncidentStatus.ClaimOpen,
                handleAction: () =>
                  openReasonDialog({
                    incidentId: incident.id,
                    action: "reject",
                  }),
              },
              {
                label: t("actions.closeNoClaim"),
                Icon: CheckCircleIcon,
                color: "zinc",
                isAdminAction: true,
                isHidden: status !== IncidentStatus.Reported,
                handleAction: () => confirmClose(IncidentClosedOutcome.NoClaim),
              },
              {
                label: t("actions.reopen"),
                Icon: ArrowPathIcon,
                color: "amber",
                isAdminAction: true,
                isHidden: status !== IncidentStatus.Closed,
                handleAction: () =>
                  openReasonDialog({
                    incidentId: incident.id,
                    action: "reopen",
                  }),
              },
            ],
          },
          {
            actions: [
              {
                label: t("actions.downloadPdf"),
                Icon: DocumentArrowDownIcon,
                isAdminAction: true,
                handleAction: async () => {
                  const { generateIncidentPdf } = await generatePdf({
                    id: incident.id,
                  });
                  window.open(generateIncidentPdf.url, "_blank");
                },
              },
            ],
          },
        ],
      },
    ];
  };
}
